<template>
	<v-menu
		:value="value"
		offset-y
		left
		:close-on-content-click="false"
		@input="$emit('input', $event)"
	>
		<template #activator="{ on, attrs }">
			<button
				type="button"
				data-testid="help-hero-recent"
				:disabled="disabled"
				class="help-recent-menu__trigger"
				v-bind="attrs"
				v-on="on"
			>
				<LucideIcon
					name="History"
					:size="14"
				/>
				Recent
			</button>
		</template>
		<div
			data-testid="help-hero-recent-menu"
			class="help-recent-menu"
		>
			<p class="help-recent-menu__label">
				Recent conversations
			</p>
			<button
				v-for="chat in visible"
				:key="chat.chatId"
				type="button"
				:data-testid="`help-hero-recent-${chat.chatId}`"
				:aria-current="chat.chatId === activeChatId ? 'true' : null"
				class="help-recent-menu__item"
				@click="$emit('select', { chatId: chat.chatId })"
			>
				<span class="help-recent-menu__title">
					{{ chat.title }}
				</span>
				<span class="help-recent-menu__time">
					{{ formatRelativeTime(chat.updatedAt) }}
				</span>
			</button>
		</div>
	</v-menu>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { formatRelativeTime } from './utils/chat-core.js'

/**
 * The splash hero's recent-conversations menu (AB#27211), the Vue counterpart of the hub's
 * `RecentConversations` in a popover. One component because the hero needs it in two places, beside
 * the idle composer and in the open conversation's toolbar, and the two hand-written copies it
 * replaced had already drifted apart.
 */
export default {
	name: 'help-recent-menu',
	components: {
		LucideIcon
	},
	props: {
		/** Menu open state, for `v-model`. */
		value: {
			type: Boolean,
			default: false
		},
		/** Conversations, already sorted by the caller (most recently updated first). */
		chats: {
			type: Array,
			default: () => []
		},
		/** Marks the matching row with `aria-current`. */
		activeChatId: {
			type: String,
			default: null
		},
		/** How many to list. */
		maxItems: {
			type: Number,
			default: 8
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		visible () {
			return this.chats.slice(0, this.maxItems)
		}
	},
	methods: {
		formatRelativeTime
	}
}
</script>

<style lang="scss" scoped>
.help-recent-menu {
	width: 320px;
	padding: 8px;
	background-color: var(--popover, #FFFFFF);
	border: 1px solid var(--border, #E0E2E5);
	border-radius: var(--radius, 8px);

	&__trigger {
		display: flex;
		gap: 6px;
		align-items: center;
		min-height: 32px;
		padding: 4px 8px;
		font-size: 0.8125rem;
		color: var(--muted-foreground, #666E7A);
		cursor: pointer;
		background: none;
		border: none;
		border-radius: var(--radius-sm, 4px);
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover:not(:disabled) {
			color: var(--foreground, #4A5568);
			background-color: var(--muted, #F5F5F5);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}
	}

	&__label {
		margin: 0 0 4px;
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--muted-foreground, #666E7A);
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	&__item {
		display: flex;
		gap: 12px;
		align-items: center;
		width: 100%;
		min-height: 40px;
		padding: 0 12px;
		font-size: 0.8125rem;
		color: var(--foreground, #4A5568);
		text-align: left;
		cursor: pointer;
		background: none;
		border: none;
		border-radius: var(--radius-sm, 4px);

		&:hover {
			background-color: var(--muted, #F5F5F5);
		}

		&[aria-current='true'] {
			font-weight: 600;
			background-color: var(--muted, #F5F5F5);
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}
	}

	&__title {
		flex: 1 1 auto;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__time {
		flex: none;
		font-size: 0.75rem;
		color: var(--muted-foreground, #666E7A);
	}
}
</style>
